// AssoKPI.jsx — Phase F : Tableau de bord administrateur (KPI consolidés)
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import {
  Users, Calendar, Euro, MessageCircle, BarChart3, TrendingUp, Activity, Ticket, Heart, Briefcase,
  Loader2, ArrowUpRight, Sparkles,
} from 'lucide-react';

const PERIODES = [
  { id: 'mois', label: 'Ce mois' },
  { id: '3mois', label: '3 derniers mois' },
  { id: 'annee', label: 'Cette année' },
  { id: 'all', label: 'Tout' },
];

export default function AssoKPI() {
  const [periode, setPeriode] = useState('3mois');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/assos/kpi?periode=${periode}`)
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(err?.response?.data?.error || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [periode]);

  return (
    <div className="page-container" data-testid="asso-kpi-page">
      <div className="page-header">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="label-uppercase mb-2 flex items-center gap-1.5" style={{ color: 'var(--theme-primary)' }}>
              <BarChart3 className="w-3 h-3" /> Analytics
            </div>
            <h1 className="page-title">Tableau de bord</h1>
            <p className="page-subtitle">Vue 360° de votre association — membres, événements, comptabilité, communications.</p>
          </div>
          {/* Sélecteur de période */}
          <div className="flex gap-1 p-1 bg-[#F1F5F9] rounded-full" data-testid="kpi-periode-selector">
            {PERIODES.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriode(p.id)}
                className={`px-3 py-1.5 text-xs font-bold rounded-full transition-all ${periode === p.id ? 'bg-white shadow text-[#0F172A]' : 'text-[#475569] hover:text-[#0F172A]'}`}
                data-testid={`kpi-periode-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" /></div>
      ) : (
        <>
          {/* KPI principaux */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="kpi-cards">
            <KpiCard icon={Users} label="Membres" value={data.membres.total} sub={`+${data.membres.nouveaux} sur période`} accent="primary" testid="kpi-membres" />
            <KpiCard icon={Calendar} label="Événements" value={data.evenements.total} sub={`${data.evenements.billets_vendus} billet${data.evenements.billets_vendus > 1 ? 's' : ''}`} testid="kpi-events" />
            <KpiCard icon={Euro} label="Recettes billetterie" value={`${data.evenements.recettes_billetterie}€`} sub="période" testid="kpi-recettes-billets" />
            <KpiCard icon={Activity} label="Taux présence" value={data.evenements.taux_presence_moyen ? `${data.evenements.taux_presence_moyen}%` : '—'} sub="moyen évts" testid="kpi-taux-presence" />
          </div>

          {/* KPI secondaires */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="kpi-secondary">
            <KpiCard icon={TrendingUp} label="Actifs 30 j" value={data.membres.actifs_30j} sub="connectés récemment" testid="kpi-actifs" mini />
            <KpiCard icon={Sparkles} label="Mensualisés" value={data.membres.mensualises} sub="cotisations auto" testid="kpi-mensualises" mini />
            <KpiCard icon={MessageCircle} label="Messages" value={data.communications.messages_periode} sub="envoyés période" testid="kpi-messages" mini />
            <KpiCard icon={Heart} label="Recommandations" value={data.networking.recommandations} sub={`${data.networking.demandes_contact} contacts`} testid="kpi-networking" mini />
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Évolution membres */}
            <div className="card !p-5" data-testid="chart-membres">
              <h3 className="font-display font-bold text-[#0F172A] flex items-center gap-2 mb-4">
                <Users className="w-4 h-4" /> Nouveaux membres / mois <span className="text-xs text-[#94A3B8] font-normal">12 derniers mois</span>
              </h3>
              {data.membres.evolution_12mois.length === 0 ? (
                <p className="text-sm text-[#475569] italic py-8 text-center">Aucune donnée disponible.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={fillMissingMonths(data.membres.evolution_12mois, 'nouveaux')}>
                    <defs>
                      <linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--theme-primary, #4DFFD2)" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="var(--theme-primary, #4DFFD2)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="mois" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E2E8F0' }} />
                    <Area type="monotone" dataKey="nouveaux" stroke="var(--theme-primary, #4DFFD2)" strokeWidth={2} fill="url(#memGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Évolution comptable */}
            <div className="card !p-5" data-testid="chart-compta">
              <h3 className="font-display font-bold text-[#0F172A] flex items-center gap-2 mb-4">
                <Euro className="w-4 h-4" /> Flux comptables / mois <span className="text-xs text-[#94A3B8] font-normal">12 derniers mois</span>
              </h3>
              {(data.comptabilite.evolution_12mois || []).length === 0 ? (
                <p className="text-sm text-[#475569] italic py-8 text-center">Aucune écriture comptable enregistrée.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={groupCompta(data.comptabilite.evolution_12mois)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="mois" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E2E8F0' }} formatter={(v) => `${v}€`} />
                    <Bar dataKey="recette" fill="#22D3A0" name="Recettes" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="depense" fill="#F87171" name="Dépenses" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Liste événements détaillée */}
          <div className="card !p-5" data-testid="kpi-events-detail">
            <h3 className="font-display font-bold text-[#0F172A] flex items-center gap-2 mb-4">
              <Ticket className="w-4 h-4" /> Performance par événement <span className="text-xs text-[#94A3B8] font-normal">{data.evenements.details.length} récents</span>
            </h3>
            {data.evenements.details.length === 0 ? (
              <p className="text-sm text-[#475569] italic py-8 text-center">Aucun événement sur la période.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-bold border-b border-[#F1F5F9]">
                      <th className="text-left py-2 px-2">Événement</th>
                      <th className="text-left py-2 px-2 hidden sm:table-cell">Date</th>
                      <th className="text-right py-2 px-2">Billets</th>
                      <th className="text-right py-2 px-2 hidden md:table-cell">Recettes</th>
                      <th className="text-right py-2 px-2">Présence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.evenements.details.map((e) => (
                      <tr key={e.id} className="border-b border-[#F9F9F8] hover:bg-[#F9F9F8] transition-colors" data-testid={`kpi-evt-row-${e.id}`}>
                        <td className="py-2.5 px-2 font-semibold text-[#0F172A] truncate max-w-xs">{e.nom}</td>
                        <td className="py-2.5 px-2 text-xs text-[#475569] hidden sm:table-cell">{new Date(e.date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' })}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums"><strong>{e.tickets_vendus}</strong>{e.capacite ? <span className="text-[#94A3B8] text-xs"> / {e.capacite}</span> : null}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums text-emerald-600 font-bold hidden md:table-cell">{e.recettes}€</td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{e.taux_presence !== null ? <span className={`font-bold ${e.taux_presence >= 80 ? 'text-emerald-600' : e.taux_presence >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>{e.taux_presence}%</span> : <span className="text-[#94A3B8]">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-6 text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">
            Dernière mise à jour : {new Date(data.genere_le).toLocaleString('fr-FR')}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent, testid, mini }) {
  return (
    <div className={`card ${mini ? '!p-3' : '!p-4'}`} style={accent === 'primary' ? { borderColor: 'var(--theme-primary)', borderWidth: 2 } : {}} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div className="label-uppercase">{label}</div>
        <Icon className="w-3.5 h-3.5 text-[#94A3B8]" />
      </div>
      <div className={`font-display ${mini ? 'text-xl' : 'text-2xl sm:text-3xl'} font-black ${accent === 'primary' ? '' : 'text-[#0F172A]'}`} style={accent === 'primary' ? { color: 'var(--theme-primary)' } : {}} data-testid={`${testid}-value`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-[#94A3B8] mt-1">{sub}</div>}
    </div>
  );
}

// Helper : remplit les mois manquants pour la courbe (12 mois glissants)
function fillMissingMonths(arr, key) {
  const map = Object.fromEntries(arr.map((p) => [p.mois, p[key]]));
  const out = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mois = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ mois: shortMois(mois), [key]: map[mois] || 0 });
  }
  return out;
}

// Helper : regroupe les mouvements comptables par mois (recette + dépense)
function groupCompta(arr) {
  const map = {};
  for (const r of arr) {
    if (!map[r.mois]) map[r.mois] = { mois: r.mois, recette: 0, depense: 0 };
    if (r.type === 'recette') map[r.mois].recette += r.montant;
    if (r.type === 'depense') map[r.mois].depense += r.montant;
  }
  // Combler les 12 derniers mois
  const now = new Date();
  const out = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mois = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const row = map[mois] || { recette: 0, depense: 0 };
    out.push({ mois: shortMois(mois), recette: row.recette, depense: row.depense });
  }
  return out;
}

function shortMois(yyyymm) {
  const [y, m] = yyyymm.split('-');
  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  return `${months[+m - 1]} ${y.slice(2)}`;
}
